import useSimpleSearch from '@/hooks/useSimpleSearch';
import NoResults from '@/components/elements/NoResults';
import { Search } from 'lucide-react';
import { TabsContent } from '@/components/elements/Tabs';
import SearchInput from '@/components/elements/SearchInput';
import AuthorNotificationSettings from '@/components/settings/AuthorNotificationSettings';
import TitleSubtitle from '@/components/elements/TitleSubtilte';
import { useNotificationSettings } from '@/hooks/useNotificationSettings';

import type { NotificationSettingsAuthor } from '@/types/models.types';

interface NotificationsProps {
  authors: NotificationSettingsAuthor[];
}

function Notifications({ authors }: NotificationsProps) {
  const { notificationStatusMap, loadingToggles, settingsLoading, handleToggleChange } = useNotificationSettings({
    authors,
  });

  const { searchTerm, handleSearchChange, clearSearch, filteredResults, isSearching } = useSimpleSearch({
    data: authors,
    searchableFields: ['name'],
  });

  return (
    <TabsContent value='notifications'>
      <header>
        <TitleSubtitle title='Notifications' subtitle='Receive an email notification when a new report is published.' />
      </header>

      <SearchInput
        value={searchTerm}
        onChange={handleSearchChange}
        onClear={clearSearch}
        placeholder='Search authors...'
        isSearching={isSearching}
        className='w-full mt-4 mb-6 md:w-80'
      />

      {filteredResults.length === 0 ? (
        <NoResults
          className='py-5 pb-8'
          title={`No authors found matching "${searchTerm}"`}
          description='Try searching for a different author.'
          icon={<Search />}
        />
      ) : (
        <ul className='mt-6 space-y-4' data-cy='notifications-list'>
          {filteredResults.map((author, index) => (
            <AuthorNotificationSettings
              key={index}
              author={author}
              handleToggleChange={handleToggleChange}
              disabled={loadingToggles[author.lookupKey] || settingsLoading}
              checked={notificationStatusMap[author.lookupKey] || false}
              hideBottomBorder={index === filteredResults.length - 1}
            />
          ))}
        </ul>
      )}
    </TabsContent>
  );
}

export default Notifications;
